<script lang="ts">
	// A4: one restaurant's account and its history with Bosla (UC-23).
	// Suspend asks for a reason in a sheet and says what it does before
	// Confirm; there is no Undo, Resume reverses it, and Resume asks in place.
	// The reason shows only here: the owner and the cashiers never see it.
	//
	// When another admin got there first (ALREADY_SUSPENDED, NOT_SUSPENDED),
	// the page says so and shows the account as it is now.
	//
	// Open owner dashboard (UC-64) opens a new tab at once, while the press
	// still counts as the admin's, then sends it to the visit link the API
	// gives. A refusal closes that tab and says why here. The Visits list
	// shows each visit whose link was followed, and opening one lists what it
	// changed: a method, a path and a status, never what was sent.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Sheet from '../components/Sheet.svelte';
	import SpecRow from '../components/SpecRow.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { copy } from './copy';
	import { flash } from './flash.svelte';
	import {
		boslaToday,
		day,
		daysBefore,
		daysFrom,
		instantDay,
		instantDayTime,
		instantTime,
		isRealDay,
	} from './format';
	import { accountChanged, accountOrigin, backFromAccount } from './nav.svelte';
	import { sessionEnded } from './session.svelte';
	import {
		visitChange,
		visitEnd,
		visitSpan,
		visitStatus,
		type Visit,
		type VisitAction,
	} from './visits';

	type Detail = components['schemas']['AdminAccountOutputBody'];

	interface Props {
		accountId: string;
	}

	let { accountId }: Props = $props();

	let detail = $state<Detail | null>(null);
	let failed = $state(false);
	let changed = $state(false);
	let sheetOpen = $state(false);
	let reason = $state('');
	let asking = $state(false);
	let busy = $state<'' | 'suspend' | 'resume' | 'end' | 'visit'>('');
	let endOpen = $state(false);
	let endDraft = $state('');
	let endInvalid = $state(false);
	let visitRefused = $state('');
	// A visit link the browser would not open in a new tab: the admin opens
	// it with a press of its own while it is good, within its minute.
	let blockedLink = $state('');
	let shownVisit = $state<Visit | null>(null);
	let actions = $state<VisitAction[] | null>(null);
	let actionsFailed = $state(false);

	const account = $derived(detail?.account);
	const suspended = $derived(account?.state === 'suspended');
	const stateLabel = $derived(
		suspended && account?.suspendedAt
			? copy.suspended(instantDay(account.suspendedAt))
			: copy.active,
	);

	async function load() {
		failed = false;
		try {
			const { data, response } = await api.GET('/api/admin/accounts/{accountId}', {
				params: { path: { accountId } },
			});
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (!data) {
				failed = true;
				return;
			}
			detail = data;
		} catch {
			failed = true;
		}
	}

	$effect(() => {
		void accountId;
		detail = null;
		changed = false;
		asking = false;
		sheetOpen = false;
		visitRefused = '';
		blockedLink = '';
		shownVisit = null;
		void load();
	});

	// The answer to a suspend or a resume: the account as it now is, or the
	// news that someone else changed it first.
	async function settle(
		result: Promise<{ data?: Detail; error?: { code?: string }; response: Response }>,
		done: string,
	): Promise<boolean> {
		try {
			const { data, error, response } = await result;
			if (response.status === 401) {
				sessionEnded();
				return false;
			}
			if (error?.code === 'ALREADY_SUSPENDED' || error?.code === 'NOT_SUSPENDED') {
				changed = true;
				await load();
				// Someone else changed it: the kept list shows it as it now is.
				if (detail) accountChanged(detail.account);
				return true;
			}
			if (!data) return false;
			detail = data;
			accountChanged(data.account);
			flash(done);
			return true;
		} catch {
			return false;
		}
	}

	async function confirmSuspend() {
		if (busy || !reason.trim()) return;
		busy = 'suspend';
		changed = false;
		const ok = await settle(
			api.POST('/api/admin/accounts/{accountId}/suspend', {
				params: { path: { accountId } },
				body: { reason: reason.trim() },
			}),
			copy.flashSuspended,
		);
		busy = '';
		if (ok) {
			sheetOpen = false;
			reason = '';
		}
	}

	async function confirmResume() {
		if (busy) return;
		busy = 'resume';
		changed = false;
		await settle(
			api.POST('/api/admin/accounts/{accountId}/resume', { params: { path: { accountId } } }),
			copy.flashResumed,
		);
		busy = '';
		asking = false;
	}

	// The subscription's end (UC-24). The days left come from the server, on
	// the restaurant's own calendar; the sheet's preview of a date not yet
	// saved counts on Bosla's, which is the same calendar today.
	const endsOn = $derived(account?.subscriptionEndsOn ?? '');
	const subscriptionText = $derived(
		endsOn ? copy.endsLine(day(endsOn), account?.subscriptionDaysLeft ?? 0) : copy.noEndDate,
	);
	const typedEnd = $derived(endDraft.trim());
	const endEffect = $derived.by(() => {
		if (!isRealDay(typedEnd) || endInvalid) return '';
		const left = daysFrom(boslaToday(), typedEnd);
		if (left < 0) return copy.effectEnded;
		if (left <= 14) return copy.effectNow;
		return copy.effectFrom(day(daysBefore(typedEnd, 14)));
	});

	function openEnd() {
		changed = false;
		endDraft = endsOn;
		endInvalid = false;
		endOpen = true;
	}

	async function saveEnd(clear: boolean) {
		if (busy) return;
		const value = clear ? '' : typedEnd;
		if (!clear && !isRealDay(value)) {
			endInvalid = true;
			return;
		}
		busy = 'end';
		try {
			const { data, error, response } = await api.PUT(
				'/api/admin/accounts/{accountId}/subscription',
				{ params: { path: { accountId } }, body: { endsOn: value } },
			);
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (error?.code === 'VALIDATION_ERROR') {
				endInvalid = true;
				return;
			}
			if (!data) return;
			detail = data;
			accountChanged(data.account);
			endOpen = false;
			flash(copy.saved);
		} catch {
			// The sheet stays open with what was typed.
		} finally {
			busy = '';
		}
	}

	// Open owner dashboard (UC-64). The tab is opened before the call, while
	// the press still counts, or the browser would block it; it is cut off
	// from this page, then sent to the link.
	async function openDashboard() {
		if (busy || suspended) return;
		busy = 'visit';
		changed = false;
		visitRefused = '';
		blockedLink = '';
		const tab = window.open('', '_blank');
		if (tab) tab.opener = null;
		try {
			const { data, error, response } = await api.POST('/api/admin/accounts/{accountId}/visit', {
				params: { path: { accountId } },
			});
			if (response.status === 401) {
				tab?.close();
				sessionEnded();
				return;
			}
			if (!data) {
				tab?.close();
				visitRefused =
					error?.code === 'ACCOUNT_SUSPENDED'
						? copy.visitSuspended
						: error?.code === 'NOT_FOUND'
							? copy.visitGone
							: copy.saveFailed;
				// A suspension another admin made shows as it now is, here and
				// in the kept list Back returns to.
				if (error?.code === 'ACCOUNT_SUSPENDED') {
					await load();
					if (detail) accountChanged(detail.account);
				}
				return;
			}
			if (!sendTab(tab, data.url)) blockedLink = data.url;
			// The history now holds the opening.
			await load();
		} catch {
			tab?.close();
			visitRefused = copy.saveFailed;
		} finally {
			busy = '';
		}
	}

	// Sends the tab opened at the press to the link. False when the browser
	// blocked it or the admin closed it meanwhile: nothing opened.
	function sendTab(tab: Window | null, url: string): boolean {
		if (!tab || tab.closed) return false;
		try {
			tab.location.replace(url);
			return true;
		} catch {
			return false;
		}
	}

	// The admin's own press, so the browser lets the tab open.
	function openBlocked() {
		const url = blockedLink;
		blockedLink = '';
		if (url) window.open(url, '_blank', 'noopener');
	}

	async function loadActions(visit: Visit) {
		actions = null;
		actionsFailed = false;
		try {
			const { data, response } = await api.GET(
				'/api/admin/accounts/{accountId}/visits/{visitId}/actions',
				{ params: { path: { accountId, visitId: visit.visitId } } },
			);
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (shownVisit?.visitId !== visit.visitId) return;
			if (!data) {
				actionsFailed = true;
				return;
			}
			actions = data.actions ?? [];
		} catch {
			if (shownVisit?.visitId === visit.visitId) actionsFailed = true;
		}
	}

	// A second press on the visit shown closes it.
	function showVisit(visit: Visit) {
		if (shownVisit?.visitId === visit.visitId) {
			shownVisit = null;
			return;
		}
		shownVisit = visit;
		void loadActions(visit);
	}

	function actionOf(kind: string, on: string | undefined): string {
		if (kind === 'suspended') return copy.actionSuspended;
		if (kind === 'resumed') return copy.actionResumed;
		if (kind === 'owner_visit_opened') return copy.actionVisitOpened;
		if (kind === 'subscription_set') {
			return on ? copy.actionSubscriptionSet(day(on)) : copy.actionSubscriptionCleared;
		}
		return kind;
	}

	const historyColumns = [
		{ label: copy.whenColumn, w: '116px' },
		{ label: copy.adminColumn, w: 'minmax(0,1fr)' },
		{ label: copy.actionColumn, w: 'minmax(0,1.2fr)' },
		{ label: copy.reasonColumn, w: 'minmax(0,1.6fr)' },
	];

	const visits = $derived(detail?.visits ?? []);
	const visitColumns = [
		{ label: copy.whenColumn, w: '116px' },
		{ label: copy.adminColumn, w: 'minmax(0,1fr)' },
		{ label: copy.timeColumn, w: '112px' },
		{ label: copy.endColumn, w: 'minmax(0,1.2fr)' },
	];
	const actionColumns = [
		{ label: copy.timeColumn, w: '64px' },
		{ label: copy.actionColumn, w: 'minmax(0,1fr)' },
		{ label: copy.statusColumn, w: '72px', align: 'end' as const },
	];
</script>

<div class="account" data-testid="admin-account">
	<div class="top">
		<Button
			testId="admin-account-back"
			label={accountOrigin() === '/subscriptions' ? copy.subscriptions : copy.accounts}
			icon="back"
			mirrorIcon
			tone="text"
			size="sm"
			onPress={backFromAccount}
		/>
		<div class="title">
			<h1>{account?.restaurantName ?? copy.account}</h1>
			{#if account}
				<Chip
					text={stateLabel}
					tone={suspended ? 'flag' : 'ok'}
					size="md"
					testId="admin-account-state"
				/>
			{/if}
		</div>
	</div>

	{#if failed}
		<Band
			tone="flag"
			action={copy.retry}
			onAction={() => void load()}
			testId="admin-account-failed"
		>
			{copy.accountFailed}
		</Band>
	{:else if !detail || !account}
		<div class="wait">
			<Loading label={copy.accountLoading} caption={copy.accountLoading} size="lg" />
		</div>
	{:else}
		{#if changed}
			<Band tone="flag" testId="admin-account-changed">
				{copy.changedTo(suspended ? copy.actionSuspended : copy.active)}
			</Band>
		{/if}
		<div class="grid">
			<Card title={copy.account} pad testId="admin-account-facts">
				<div class="facts">
					<SpecRow label={copy.owner} value={account.ownerName} mono={false} />
					<SpecRow label={copy.email} value={account.ownerEmail} />
					<SpecRow label={copy.signedUp} value={instantDay(account.signedUpAt)} />
					<SpecRow label={copy.shops} value={String(account.shopCount)} />
					<SpecRow label={copy.tills} value={String(account.tillCount)} />
					<SpecRow
						label={copy.lastOrder}
						value={account.lastOrderDate ? day(account.lastOrderDate) : copy.noOrders}
						mono={!!account.lastOrderDate}
					/>
					<SpecRow label={copy.subscription} value={subscriptionText} mono={false} />
				</div>
			</Card>
			<div class="actions">
				{#if visitRefused}
					<Band tone="flag" testId="admin-visit-refused">{visitRefused}</Band>
				{/if}
				{#if blockedLink}
					<Band
						tone="flag"
						action={copy.openDashboard}
						actionTestId="admin-visit-open-blocked"
						onAction={openBlocked}
						testId="admin-visit-blocked"
					>
						{copy.visitBlocked}
					</Band>
				{/if}
				{#if !asking}
					<div class="row wrap">
						{#if suspended}
							<Button
								testId="admin-account-resume"
								label={copy.resume}
								tone="secondary"
								size="md"
								disabled={busy === 'visit'}
								onPress={() => {
									changed = false;
									visitRefused = '';
									asking = true;
								}}
							/>
						{:else}
							<Button
								testId="admin-account-suspend"
								label={copy.suspend}
								tone="secondary"
								size="md"
								disabled={busy === 'visit'}
								onPress={() => {
									changed = false;
									visitRefused = '';
									sheetOpen = true;
								}}
							/>
						{/if}
						<Button
							testId="admin-account-visit"
							label={copy.openDashboard}
							tone="secondary"
							size="md"
							disabled={suspended || (busy !== '' && busy !== 'visit')}
							state={busy === 'visit' ? 'pending' : 'idle'}
							pendingLabel={copy.openingDashboard}
							onPress={() => void openDashboard()}
						/>
					</div>
				{/if}
				{#if suspended && asking}
					<div class="ask" data-testid="admin-account-resume-ask">
						<p>{copy.resumeQuestion(account.restaurantName)}</p>
						<div class="row wrap">
							<Button
								testId="admin-account-resume-yes"
								label={copy.resume}
								tone="primary"
								size="md"
								state={busy === 'resume' ? 'pending' : 'idle'}
								pendingLabel={copy.resuming}
								onPress={() => void confirmResume()}
							/>
							<Button
								testId="admin-account-resume-no"
								label={copy.no}
								tone="secondary"
								size="md"
								disabled={busy === 'resume'}
								onPress={() => (asking = false)}
							/>
						</div>
					</div>
				{/if}
				<p class="note">{suspended ? copy.resumeNote : copy.suspendNote}</p>
				<p class="note" data-testid="admin-visit-note">{copy.openDashboardNote}</p>
				<div class="end">
					<Button
						testId="admin-account-subscription"
						label={copy.subscriptionEnd}
						tone="secondary"
						size="md"
						disabled={busy === 'suspend' || busy === 'resume'}
						onPress={openEnd}
					/>
					<p class="note">{copy.subscriptionEndNote}</p>
				</div>
			</div>
		</div>

		<TablePanel
			columns={historyColumns}
			title={copy.history}
			note={copy.historyNote}
			testId="admin-account-history"
		>
			{#each detail.events as ev, i (i)}
				<Row testId="admin-account-event-{i}">
					<span class="mono small">{instantDayTime(ev.at)}</span>
					<span class="small wrap-any">{ev.adminEmail}</span>
					<span class="small strong">{actionOf(ev.kind, ev.endsOn)}</span>
					<span class="small body">{ev.reason ?? ''}</span>
				</Row>
			{:else}
				<Row testId="admin-account-no-history">
					<span class="small body all">{copy.neverSuspended}</span>
				</Row>
			{/each}
		</TablePanel>

		<TablePanel
			columns={visitColumns}
			title={copy.visits}
			note={copy.visitsNote}
			testId="admin-account-visits"
		>
			{#each visits as visit, i (visit.visitId)}
				<Row
					as="button"
					current={shownVisit?.visitId === visit.visitId}
					onPress={() => showVisit(visit)}
					testId="admin-account-visit-{i}"
				>
					<span class="mono small">{instantDayTime(visit.startedAt)}</span>
					<span class="small wrap-any">{visit.adminEmail}</span>
					<span class="mono small">{visitSpan(visit)}</span>
					<span class="small strong" data-testid="admin-account-visit-{i}-end"
						>{visitEnd(visit)}</span
					>
				</Row>
			{:else}
				<Row testId="admin-account-no-visits">
					<span class="small body all">{copy.noVisits}</span>
				</Row>
			{/each}
		</TablePanel>

		{#if shownVisit}
			<TablePanel
				columns={actionColumns}
				title={copy.visitChanges(instantDayTime(shownVisit.startedAt), shownVisit.adminEmail)}
				note={copy.visitChangesNote}
				testId="admin-visit-actions"
			>
				{#if actionsFailed}
					<div class="all pad">
						<Band
							tone="flag"
							action={copy.retry}
							onAction={() => shownVisit && void loadActions(shownVisit)}
							testId="admin-visit-actions-failed"
						>
							{copy.actionsFailed}
						</Band>
					</div>
				{:else if actions === null}
					<div class="all wait-sm">
						<Loading label={copy.actionsLoading} />
					</div>
				{:else}
					{#each actions as action, i (i)}
						<Row testId="admin-visit-action-{i}">
							<span class="mono small">{instantTime(action.at)}</span>
							<span class="mono small wrap-any">{visitChange(action)}</span>
							<span class="mono small figure">{visitStatus(action)}</span>
						</Row>
					{:else}
						<Row testId="admin-visit-no-actions">
							<span class="small body all">{copy.noActions}</span>
						</Row>
					{/each}
				{/if}
			</TablePanel>
		{/if}
	{/if}
</div>

<Sheet
	open={sheetOpen}
	title={copy.suspendTitle(account?.restaurantName ?? '')}
	onClose={() => {
		if (!busy) sheetOpen = false;
	}}
	closeLabel={copy.close}
	primaryLabel={copy.confirm}
	primaryIcon="check"
	onPrimary={() => void confirmSuspend()}
	primaryDisabled={!reason.trim() || busy !== ''}
	primaryState={busy === 'suspend' ? 'pending' : 'idle'}
	pendingLabel={copy.suspending}
	locked={busy === 'suspend'}
	testId="admin-suspend-sheet"
	primaryTestId="admin-suspend-confirm"
>
	<div class="sheet">
		<Band tone="flag" testId="admin-suspend-effect">{copy.suspendEffect}</Band>
		<p class="drawer">{copy.suspendDrawer}</p>
		<Field
			testId="admin-suspend-reason"
			label={copy.reason}
			value={reason}
			rows={3}
			maxLength={500}
			placeholder={copy.reasonPlaceholder}
			note={reason.trim() ? copy.reasonCount(reason.length) : copy.reasonRequired}
			onInput={(value) => (reason = value.slice(0, 500))}
		/>
		<p class="note">{copy.noUndo}</p>
	</div>
</Sheet>

<Sheet
	open={endOpen}
	title={copy.endSheetTitle(account?.restaurantName ?? '')}
	onClose={() => {
		if (busy !== 'end') endOpen = false;
	}}
	closeLabel={copy.close}
	primaryLabel={copy.confirm}
	primaryIcon="check"
	onPrimary={() => void saveEnd(false)}
	primaryDisabled={!typedEnd || typedEnd === endsOn || busy === 'end'}
	primaryState={busy === 'end' ? 'pending' : 'idle'}
	pendingLabel={copy.saving}
	secondaryLabel={endsOn ? copy.noEndDate : undefined}
	onSecondary={() => void saveEnd(true)}
	secondaryDisabled={busy === 'end'}
	secondaryTestId="admin-end-clear"
	locked={busy === 'end'}
	testId="admin-end-sheet"
	primaryTestId="admin-end-confirm"
>
	<div class="sheet">
		<div class="date">
			<Field
				testId="admin-end-date"
				label={copy.endsOn}
				value={endDraft}
				mono
				maxLength={10}
				placeholder="2026-12-31"
				note={copy.endsOnNote}
				error={endInvalid ? copy.invalidDate : undefined}
				errorTestId="admin-end-invalid"
				onInput={(value) => {
					endDraft = value.slice(0, 10);
					endInvalid = false;
				}}
			/>
		</div>
		{#if endEffect}
			<p class="effect" data-testid="admin-end-effect">{endEffect}</p>
		{/if}
		<p class="note">
			{endsOn ? copy.currentEnds(subscriptionText) : copy.currentNone}
		</p>
	</div>
</Sheet>

<style>
	.end {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.date {
		max-inline-size: 280px;
	}

	.effect {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--text-strong);
	}

	.account {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.top {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
	}

	.title {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
		gap: var(--grid-gap);
		align-items: start;
	}

	.facts {
		display: flex;
		flex-direction: column;
	}

	.actions {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.row {
		display: flex;
	}

	.row.wrap {
		flex-wrap: wrap;
		gap: var(--space-4);
	}

	.ask {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		padding: var(--card-pad);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
	}

	.ask p {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.note {
		margin: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.mono {
		min-inline-size: 0;
		font-family: var(--font-mono);
	}

	.small {
		min-inline-size: 0;
		font-size: var(--text-sm);
	}

	.strong {
		font-weight: 700;
	}

	.body {
		color: var(--text-body);
		text-wrap: pretty;
	}

	.wrap-any {
		overflow-wrap: anywhere;
	}

	.all {
		grid-column: 1 / -1;
	}

	.pad {
		padding: var(--row-pad);
	}

	.wait-sm {
		display: grid;
		place-items: center;
		padding: var(--space-7);
	}

	.figure {
		text-align: end;
	}

	.sheet {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.drawer {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}
</style>
